import React from "react";
import { FadeTransition } from "../../../pages/get-compliant/FadeTransition";
import clsx from "clsx";
import { BaseQuestion, BaseQuestionProps } from "./BaseQuestion";
import { BooleanQuestion } from "../../organisms/survey/BooleanQuestion";

export type SurveyBooleanQuestionProps = BaseQuestion & {
  type: "boolean";

  yesLabel?: string;
  noLabel?: string;

  yesDisabled?: boolean;
  yesDisabledTooltip?: string;
  yesConfirmation?: React.ReactNode;
  noDisabled?: boolean;
  noDisabledTooltip?: string;
  noConfirmation?: React.ReactNode;

  noFirst?: boolean;

  // When the question is disabled via the survey's `disabled` map, this controls which answer
  // to lock to. Defaults to "true" (Yes). Set to "false" for negatively-framed questions like
  // "Has your business turned off X?" where disabled means the answer is "No".
  disabledAnswer?: "true" | "false";
};

type Props = BaseQuestionProps & {
  question: SurveyBooleanQuestionProps;
  useNewDesign?: boolean;
  onlyShowInlineAnswerButtons?: boolean; // only applicable if `useNewDesign` is true
};

export const SurveyBooleanQuestion: React.FC<Props> = ({
  question,
  answer,
  onAnswer,
  useNewDesign,
  ...rest
}) => {
  // For questions with `onlyAnswerableFromDashboard` set to true, skip the visibility check since those questions will always initially be not visible
  if (question.notPresent && !question.onlyAnswerableFromDashboard) {
    return null;
  }

  const lockedAnswer = question.disabledAnswer ?? "true";
  const isLockedToYes = question.isDisabled && lockedAnswer === "true";
  const isLockedToNo = question.isDisabled && lockedAnswer === "false";
  const effectiveAnswer = question.isDisabled ? lockedAnswer : answer;

  let questionEl = (
    <BooleanQuestion
      question={question.question}
      header={question.header}
      description={question.description}
      recommendation={question.recommendation}
      helpText={question.helpText}
      answer={effectiveAnswer}
      disabled={question.isDisabled}
      disabledTooltip={question.disabledReason}
      setAnswer={onAnswer}
      yesLabel={question.yesLabel}
      yesDisabled={isLockedToNo || question.yesDisabled}
      yesDisabledTooltip={isLockedToNo ? question.disabledReason : question.yesDisabledTooltip}
      yesConfirmation={question.yesConfirmation}
      noLabel={question.noLabel}
      noDisabled={isLockedToYes || question.noDisabled}
      noDisabledTooltip={isLockedToYes ? question.disabledReason : question.noDisabledTooltip}
      noConfirmation={question.noConfirmation}
      noFirst={question.noFirst}
      useNewDesign={useNewDesign}
      {...rest}
    />
  );

  if (useNewDesign) {
    return questionEl;
  } else {
    return (
      <FadeTransition
        key={`question-${question.slug}`}
        cls="survey-question-fade"
        in={!!answer || question.visible || question.onlyAnswerableFromDashboard}
        unmountOnExit
        appear
      >
        <div
          className={clsx("survey--question", {
            "survey-question--top-margin": !rest.collapseTopSpacing,
          })}
          ref={question.ref}
        >
          {questionEl}
        </div>
      </FadeTransition>
    );
  }
};

export const SurveyQuestionContainer = ({ children, offsetFirst = false, ...props }) => {
  const className = clsx("intro-survey--questions-container", {
    "intro-survey--questions-container-offset-first": offsetFirst,
  });
  return (
    <div className={className} {...props}>
      {children}
    </div>
  );
};
